import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store';
import App from './components/index';
import { initializeDevEnvironment } from './services/devInitializer';
import { isInIframe, isLocalEnvironment, requiresDirectBrowserAuth } from './utils/environmentDetection';
import { ensureAuthenticated, SIGN_IN_BLOCKED_MESSAGE } from './services/midwayAuth';

// Initialize development environment (only runs in local environment)
initializeDevEnvironment();

// Diagnostic only — logs how the app was loaded (embedded in the Boson shell
// vs. a top-level browser tab such as a personal stack / standalone deploy).
// Pure observation: it changes no behavior and is wrapped so a failure here can
// never block rendering.
try {
  const framed = isInIframe();
  console.log(
    `[RCCFrontend] startup load context: ${framed ? 'IFRAMED (Boson shell parent)' : 'TOP-LEVEL (direct browser tab / personal stack)'}`,
    { isInIframe: framed, isLocalEnvironment: isLocalEnvironment() }
  );
} catch (error) {
  console.warn('[RCCFrontend] startup load-context log failed (non-fatal):', error);
}

const container = document.getElementById('root');
const root = createRoot(container!);

function renderApp(): void {
    root.render(
        <React.StrictMode>
            <Provider store={store}>
                <App />
            </Provider>
        </React.StrictMode>
    );
}

/** Minimal inline message shown only if the Midway loop guard trips (no app chrome available yet). */
function renderSignInError(message: string): void {
    root.render(
        <div role="alert" style={{ fontFamily: 'Amazon Ember, Arial, sans-serif', padding: '48px', textAlign: 'center' }}>
            <h2>Sign-in required</h2>
            <p>{message}</p>
        </div>
    );
}

// Auth gate.
//   - Framed (Boson shell) -> the shell already holds the Midway/TCP session and
//     supplies identity over postMessage, so render immediately; a Midway redirect
//     here would be blocked anyway (Midway cannot be framed).
//   - Top-level, non-local, command-center host -> run the Midway sign-in gate
//     and render only after the user is authenticated.
//   - Anything else (local dev, unlisted host) -> render immediately
//     (requiresDirectBrowserAuth() fails open by design).
if (!requiresDirectBrowserAuth()) {
    console.log(
        `[RCCFrontend] startup load context: ${isInIframe() ? 'IFRAMED (Boson shell supplies auth)' : 'TOP-LEVEL (no Midway gate for this host)'}`
    );
    renderApp();
} else {
    console.log('[RCCFrontend] startup load context: TOP-LEVEL direct load — running Midway sign-in gate');
    ensureAuthenticated()
        .then(() => renderApp())
        .catch((error: unknown) => {
            // Only reached when the redirect loop guard gives up (e.g. cookies blocked).
            const message = error instanceof Error ? error.message : SIGN_IN_BLOCKED_MESSAGE;
            console.error('[RCCFrontend] Midway sign-in gate failed:', error);
            renderSignInError(message);
        });
}
